import React from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import type { AgenticAnalysisSummary as AgenticAnalysisSummaryType } from '../AskAsperAgentic.types';
import XClose from '../../../atoms/Icon/XClose';

export interface AgenticAnalysisSummaryProps {
    summary: AgenticAnalysisSummaryType;
    'data-test'?: string;
    'data-test-id'?: string;
}

/**
 * AgenticAnalysisSummary - Overall conversation summary component
 *
 * Displays:
 * - Key findings as bullet points
 * - Additional insight box
 * - Recommendations
 */
const AgenticAnalysisSummary: React.FC<AgenticAnalysisSummaryProps> = ({
    summary,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();

    // Parse text with **bold** markers
    const parseBoldText = (text: string) => {
        return text.split(/(\*\*.*?\*\*)/g).map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                const boldText = part.slice(2, -2);
                return (
                    <strong key={index} className="font-semibold">
                        {boldText}
                    </strong>
                );
            }
            return <span key={index}>{part}</span>;
        });
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className="bg-white rounded-lg border border-[rgba(0,0,0,0.06)] shadow-sm p-6"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-analysis-summary
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-['Open_Sans'] font-semibold text-[16px] leading-[24px] text-black">
                        Analysis Summary
                    </h3>
                    <div className="flex items-center gap-2">
                        {summary.onShare && (
                            <button
                                onClick={summary.onShare}
                                className="p-1 hover:bg-[rgba(0,0,0,0.04)] rounded transition-colors"
                                data-test-id={`${dataTestId}-share`}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M8 2v6M5 5l3-3 3 3M2 10h12" stroke="currentColor" strokeWidth="1.5" />
                                </svg>
                            </button>
                        )}
                        {summary.onClose && (
                            <button
                                onClick={summary.onClose}
                                className="p-1 hover:bg-[rgba(0,0,0,0.04)] rounded transition-colors"
                                data-test-id={`${dataTestId}-close`}
                            >
                                <XClose size={16} color="rgba(0,0,0,0.45)" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Content - supports string or array of sentences */}
                {summary.content && (
                    <div className="mb-4">
                        <ul className="list-disc pl-5 space-y-2 m-0">
                            {(Array.isArray(summary.content) ? summary.content : summary.content.split('\n'))
                                .map((line) => line.trim())
                                .filter((line) => line.length > 0)
                                .map((line, index) => (
                                    <li
                                        key={index}
                                        className="font-['Open_Sans'] text-[14px] leading-[20px] text-[rgba(0,0,0,0.88)]"
                                    >
                                        {parseBoldText(line)}
                                    </li>
                                ))}
                        </ul>
                    </div>
                )}

                {/* Key Findings */}
                {summary.keyFindings && summary.keyFindings.length > 0 && (
                    <div className="mb-4">
                        <ul className="list-disc list-inside space-y-2 ml-2">
                            {summary.keyFindings.map((finding, index) => (
                                <li
                                    key={index}
                                    className="font-['Open_Sans'] text-[14px] leading-[20px] text-[rgba(0,0,0,0.88)]"
                                >
                                    {parseBoldText(finding.text)}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Additional Insight Box */}
                {summary.additionalInsight && (
                    <div className="bg-[#F5F5F5] rounded-lg px-4 py-3 mb-4 flex items-start gap-2">
                        {summary.additionalInsight.icon && (
                            <div className="flex-shrink-0 mt-0.5">{summary.additionalInsight.icon}</div>
                        )}
                        <div className="flex-1 font-['Open_Sans'] text-[14px] leading-[20px] text-[rgba(0,0,0,0.88)]">
                            {parseBoldText(summary.additionalInsight.text)}
                        </div>
                    </div>
                )}

                {/* Recommendations */}
                {summary.recommendations && summary.recommendations.length > 0 && (
                    <div>
                        <h4 className="font-['Open_Sans'] font-semibold text-[14px] leading-[20px] text-black mb-2">
                            Recommendations
                        </h4>
                        <ul className="list-disc list-inside space-y-2 ml-2">
                            {summary.recommendations.map((recommendation, index) => (
                                <li
                                    key={index}
                                    className="font-['Open_Sans'] text-[14px] leading-[20px] text-[rgba(0,0,0,0.88)]"
                                >
                                    {recommendation}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default AgenticAnalysisSummary;
